'use client'

import { useCallback, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

type ParamValue = string | number | boolean | string[] | undefined

interface IUseQueryParamsReturn<T extends Record<string, ParamValue>> {
    /** Reactive params object derived from `useSearchParams()` — triggers re-renders on URL changes. */
    params: Partial<T>
    /** Imperative read — reads from `window.location.search`, does NOT trigger re-renders. */
    getParam: <K extends keyof T>(key: K) => T[K] | undefined
    /** Imperative read — reads from `window.location.search`, does NOT trigger re-renders. */
    getAllParams: () => Partial<T>
    setParam: <K extends keyof T>(key: K, value: T[K]) => void
    setMultipleParams: (params: Partial<T>) => void
    setAllParams: (newParams: T) => void
    deleteManyParams: (keys: (keyof T)[]) => void
    deleteParam: <K extends keyof T>(key: K) => void
    deleteAllParams: () => void
}

function serializeParam(value: ParamValue): string | undefined {
    try {
        if (value === undefined) return undefined

        if (typeof value === 'string') return encodeURIComponent(value)

        return encodeURIComponent(JSON.stringify(value))
    } catch {
        return undefined
    }
}

function deserializeParam<T extends ParamValue>(value: string | null | undefined): T | undefined {
    try {
        if (value === null || value === undefined) return undefined

        const decoded = decodeURIComponent(value)

        try {
            return JSON.parse(decoded) as T
        } catch {
            return decoded as T
        }
    } catch {
        return undefined
    }
}

/**
 * Hook to read and write URL query params.
 *
 * - `params` — reactive object derived from `useSearchParams()`, triggers re-renders on URL changes.
 * - `getParam` / `getAllParams` — imperative reads from `window.location.search`, no re-renders.
 *
 * @param options.mode - `'replace'` (default) or `'push'` — controls whether writes use `router.replace` or `router.push`.
 */
export function useQueryParams<T extends Record<string, ParamValue>>({
    mode = 'replace',
}: {
    mode?: 'replace' | 'push'
} = {}): IUseQueryParamsReturn<T> {
    const { replace, push } = useRouter()
    const searchParams = useSearchParams()

    /** Read current pathname from window.location to avoid stale React state during navigations. */
    const getCurrentPathname = useCallback(() => window.location.pathname, [])

    /** Reactive params — re-renders when URL search params change. */
    const params = useMemo((): Partial<T> => {
        const result: Partial<T> = {}

        searchParams.forEach((value, key) => {
            result[key as keyof T] = deserializeParam<T[keyof T]>(value)
        })

        return result
    }, [searchParams])

    /**
     * Imperative read of all params from window.location.search.
     */
    const getAllParams = useCallback((): Partial<T> => {
        if (typeof window === 'undefined') return {} as Partial<T>

        const params = new URLSearchParams(window.location.search)

        const result: Partial<T> = {}

        params.forEach((value, key) => {
            result[key as keyof T] = deserializeParam<T[keyof T]>(value)
        })

        return result
    }, [])

    /**
     * Imperative read of a single param from window.location.search.
     */
    const getParam = useCallback(<K extends keyof T>(key: K): T[K] | undefined => {
        if (typeof window === 'undefined') return undefined

        const params = new URLSearchParams(window.location.search)

        const value = params.get(key as string)

        return deserializeParam<T[K]>(value)
    }, [])

    /**
     * A function to set all params.
     */
    const setAllParams = useCallback(
        (newParams: T): void => {
            const params = new URLSearchParams()

            Object.entries(newParams).forEach(([key, value]) => {
                const serialized = serializeParam(value)

                if (serialized !== undefined) {
                    params.set(key, serialized)
                }
            })

            const path = `${getCurrentPathname()}?${params.toString()}`

            if (mode === 'replace') {
                replace(path)
            } else {
                push(path)
            }
        },
        [mode, getCurrentPathname, push, replace]
    )

    /**
     * A function to set multiple params.
     * If a param is not provided, it will stay unmodified.
     * If a param is set to undefined explicitly, it will be deleted.
     */
    const setMultipleParams = useCallback(
        (newParams: Partial<T>): void => {
            const params = new URLSearchParams(window.location.search)

            Object.entries(newParams).forEach(([key, value]) => {
                if (value === undefined) {
                    params.delete(key)
                } else {
                    const serialized = serializeParam(value)

                    if (serialized !== undefined) {
                        params.set(key, serialized)
                    }
                }
            })

            const path = `${getCurrentPathname()}?${params.toString()}`

            if (mode === 'replace') {
                replace(path)
            } else {
                push(path)
            }
        },
        [mode, getCurrentPathname, push, replace]
    )

    /**
     * A function to set a single param.
     * All existing params will stay unmodified.
     */
    const setParam = useCallback(
        <K extends keyof T>(key: K, value: T[K]): void => {
            // Use window.location to ensure we get the most current pathname and params
            // This avoids issues with stale React state during navigations
            const params = new URLSearchParams(window.location.search)

            const serialized = serializeParam(value)

            if (serialized !== undefined) {
                params.set(key as string, serialized)
            }

            const path = `${getCurrentPathname()}?${params.toString()}`

            if (mode === 'replace') {
                replace(path)
            } else {
                push(path)
            }
        },
        [mode, getCurrentPathname, push, replace]
    )

    /**
     * A function to delete a single param.
     * All existing params will stay unmodified.
     */
    const deleteParam = useCallback(
        <K extends keyof T>(key: K): void => {
            const params = new URLSearchParams(window.location.search)

            params.delete(key as string)

            const path = `${getCurrentPathname()}?${params.toString()}`

            if (mode === 'replace') {
                replace(path)
            } else {
                push(path)
            }
        },
        [mode, getCurrentPathname, push, replace]
    )

    /**
     * A function to delete multiple params.
     * All other params will stay unmodified.
     */
    const deleteManyParams = useCallback(
        (keys: (keyof T)[]): void => {
            const params = new URLSearchParams(window.location.search)

            keys.forEach((key) => params.delete(key as string))

            const path = `${getCurrentPathname()}?${params.toString()}`

            if (mode === 'replace') {
                replace(path)
            } else {
                push(path)
            }
        },
        [mode, getCurrentPathname, push, replace]
    )

    /**
     * A function to delete all params.
     */
    const deleteAllParams = useCallback((): void => {
        replace(getCurrentPathname())
    }, [getCurrentPathname, replace])

    return {
        params,
        getAllParams,
        getParam,
        setAllParams,
        setMultipleParams,
        setParam,
        deleteParam,
        deleteManyParams,
        deleteAllParams,
    }
}
